import { ReactNode, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Loading from "./Loading";
import usePermissionStore from "../store/permission";
import { SIDE_MENU_CONFIG } from "./SideMenu";

export type PermissionRequirement = string | string[];

export const hasRequiredPermission = (
  permissions: string[],
  requirement?: PermissionRequirement
) => {
  if (!requirement) {
    return true;
  }
  const required = Array.isArray(requirement) ? requirement : [requirement];
  return required.some((req) => permissions.includes(req));
};

export const getFirstAllowedPath = (permissions: string[]) => {
  const allowedItem = SIDE_MENU_CONFIG.find(
    (item) => hasRequiredPermission(permissions, item.permission)
  );
  return allowedItem?.path ?? "/no-permission";
};

interface PermissionGuardProps {
  permission?: PermissionRequirement;
  children: ReactNode;
}

const PermissionGuard = ({ permission, children }: PermissionGuardProps) => {
  const { isReady, permissions } = usePermissionStore((state) => ({
    isReady: state.isReady,
    permissions: state.permissions,
  }));
  const isAllowed = hasRequiredPermission(permissions, permission);
  const location = useLocation();

  const defaultPath = useMemo(() => {
    const allowedPath = getFirstAllowedPath(permissions);
    const alternativeItem = SIDE_MENU_CONFIG.find((item) => {
      const satisfied = hasRequiredPermission(permissions, item.permission);
      return satisfied && item.path !== location.pathname;
    });
    return alternativeItem?.path ?? allowedPath;
  }, [permissions, location.pathname]);

  if (!isReady) {
    return <Loading open />;
  }

  if (!isAllowed) {
    return <Navigate to={defaultPath} replace />;
  }

  return <>{children}</>;
};

export const DefaultRedirect = () => {
  const { isReady, permissions } = usePermissionStore((state) => ({
    isReady: state.isReady,
    permissions: state.permissions,
  }));

  if (!isReady) {
    return <Loading open />;
  }

  const destination = getFirstAllowedPath(permissions);
  return <Navigate to={destination} replace />;
};

export default PermissionGuard;

